import { buildMetadataPreview, formatMetadataValue } from './metadata-preview';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/v1/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/v1/ui/tooltip';
import { cn } from '@/lib/utils';
import { TagIcon } from '@heroicons/react/24/outline';
import { useMemo } from 'react';

export interface AdditionalMetadataClick {
  key: string;
  value: string;
}

interface AdditionalMetadataProps {
  metadata: object;
  onClick?: (click: AdditionalMetadataClick) => void;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  align?: 'start' | 'center' | 'end';
}

function MetadataDetails({
  entries,
  onSelect,
}: {
  entries: Array<[string, unknown]>;
  onSelect?: (click: AdditionalMetadataClick) => void;
}) {
  return (
    <div className="max-h-[min(15rem,var(--radix-popover-content-available-height))] space-y-2 overflow-y-auto">
      {entries.map(([key, value]) => {
        const filterValue = typeof value === 'string' ? value : undefined;
        const canFilter =
          Boolean(onSelect) &&
          filterValue !== undefined &&
          key.length > 0 &&
          !key.includes(':');
        const fullValue = formatMetadataValue(value, 2_000);
        const content = (
          <>
            <span className="text-xs font-medium tracking-wide text-muted-foreground">
              {key}
            </span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    tabIndex={canFilter ? undefined : 0}
                    aria-label={`${key}: ${fullValue}`}
                    className="truncate text-sm"
                  >
                    {formatMetadataValue(value)}
                  </span>
                </TooltipTrigger>
                <TooltipContent
                  side="left"
                  className="max-h-64 max-w-xs overflow-auto"
                >
                  <pre className="whitespace-pre-wrap break-all text-xs">
                    {JSON.stringify(value, null, 2)}
                  </pre>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </>
        );

        return canFilter ? (
          <button
            type="button"
            key={key}
            aria-label={`Filter by ${key}: ${fullValue}`}
            className="group flex w-full cursor-pointer flex-col gap-1 rounded border p-2 text-left transition-colors hover:bg-muted/30"
            onClick={() => onSelect?.({ key, value: filterValue ?? '' })}
          >
            {content}
          </button>
        ) : (
          <div key={key} className="flex flex-col gap-1 rounded border p-2">
            {content}
          </div>
        );
      })}
    </div>
  );
}

function MetadataChip({
  displayValue,
  index,
  metadataKey,
  onSelect,
  value,
}: {
  displayValue: string;
  index: number;
  metadataKey: string;
  onSelect?: (click: AdditionalMetadataClick) => void;
  value: unknown;
}) {
  const filterValue = typeof value === 'string' ? value : undefined;
  const canFilter =
    Boolean(onSelect) &&
    filterValue !== undefined &&
    metadataKey.length > 0 &&
    !metadataKey.includes(':');
  const className = cn(
    'inline-flex h-6 max-w-36 items-center overflow-hidden rounded border font-mono text-[11px] leading-4 text-foreground transition-colors',
    index === 2 && 'hidden 2xl:inline-flex',
    canFilter &&
      'cursor-pointer hover:border-foreground/40 hover:bg-muted/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
  );
  const title = `${canFilter ? 'Filter by ' : ''}${metadataKey}=${
    typeof value === 'object' && value !== null
      ? displayValue
      : formatMetadataValue(value, 200)
  }`;
  const content = (
    <>
      <span className="max-w-20 truncate bg-card px-1.5 text-muted-foreground">
        {metadataKey}
      </span>
      <span className="max-w-20 truncate border-l bg-muted px-1.5">
        {displayValue}
      </span>
    </>
  );

  if (!canFilter) {
    return (
      <span title={title} className={className}>
        {content}
      </span>
    );
  }

  return (
    <button
      type="button"
      data-testid={`metadata-filter-${metadataKey}`}
      aria-label={title}
      title={title}
      className={className}
      onClick={(event) => {
        event.stopPropagation();
        onSelect?.({ key: metadataKey, value: filterValue });
      }}
    >
      {content}
    </button>
  );
}

export function AdditionalMetadata({
  metadata,
  onClick,
  isOpen,
  onOpenChange,
  title = 'Metadata',
  align = 'end',
}: AdditionalMetadataProps) {
  const { entries, remaining, visible } = useMemo(
    () => buildMetadataPreview(metadata || {}),
    [metadata],
  );

  if (entries.length === 0) {
    return null;
  }

  const mobileRemaining = Math.max(0, entries.length - 2);

  return (
    <div
      data-testid="metadata-preview"
      data-cy="metadata-preview"
      className="flex min-w-0 max-w-[30rem] items-center justify-start gap-1"
    >
      <Popover open={isOpen} onOpenChange={onOpenChange}>
        {visible.map(({ displayValue, key, value }, index) => (
          <MetadataChip
            key={key}
            metadataKey={key}
            displayValue={displayValue}
            value={value}
            index={index}
            onSelect={onClick}
          />
        ))}
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={`Show all ${entries.length} metadata ${entries.length === 1 ? 'item' : 'items'}`}
            title="Show all metadata"
            className="inline-flex h-6 shrink-0 cursor-pointer items-center rounded border bg-muted/30 px-1.5 font-mono text-[11px] leading-4 text-muted-foreground transition-colors hover:border-foreground/40 hover:bg-muted/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            onClick={(event) => event.stopPropagation()}
          >
            <span className="2xl:hidden">
              {mobileRemaining > 0 ? (
                `+${mobileRemaining}`
              ) : (
                <TagIcon className="size-3" />
              )}
            </span>
            <span className="hidden 2xl:inline-flex">
              {remaining > 0 ? `+${remaining}` : <TagIcon className="size-3" />}
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[min(20rem,calc(100vw-2rem))] p-0 shadow-md shadow-black/40"
          align={align}
        >
          <div className="p-3">
            <div className="mb-3 flex items-center gap-2 border-b pb-2">
              <TagIcon className="size-4 text-muted-foreground" />
              <span className="text-sm font-medium">
                {title} ({entries.length}{' '}
                {entries.length === 1 ? 'item' : 'items'})
              </span>
            </div>
            <MetadataDetails
              entries={entries}
              onSelect={
                onClick
                  ? (click) => {
                      onClick(click);
                      onOpenChange(false);
                    }
                  : undefined
              }
            />
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
